import { Spin } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { ArrowDown } from "../../../assets/icons/ArrowDown";
import { AUTH_PORTAL_PREFERENCES } from "../../../constants/localStorageKeys";
import {
  DEFAULT_VIEW_TYPE,
  viewTypeOptions,
} from "../../../constants/viewTypes";
import { useUserPreferences } from "../../../hooks/useUserPreferences";
import { userService } from "../../../services/userService";
import { isProduction } from "../../../utils/environment";
import { mixpanelActions } from "../../../utils/mixpanel";
import { safeStorage } from "../../../utils/storage";
import {
  SavingOverlay,
  ViewTypeOption,
  ViewTypeOptionsContainer,
  ViewTypeOptionsFooter,
  ViewTypeSelectorButton,
} from "./ViewTypeSelector.styles";

// Patient details routes look like /patients/<numeric id>/<tab>; the patient
// list variants (/patients/active, /patients/archived, ...) must not match.
const patientDetailsPathRegex = /^\/patients\/\d+(\/|$)/;

export function ViewTypeSelector() {
  const { t } = useTranslation();
  const [showOptions, setShowOptions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const optionsRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const { portalPreferences, changePortalPreferences } = useUserPreferences();

  const selectedViewType =
    portalPreferences?.selectedViewType ?? DEFAULT_VIEW_TYPE;
  const selectedOption =
    viewTypeOptions.find((option) => option.value === selectedViewType) ??
    viewTypeOptions[0];

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        showOptions &&
        optionsRef.current &&
        !optionsRef.current.contains(event.target)
      ) {
        setShowOptions(false);
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [showOptions]);

  // The view type selector only appears on the patient details page
  if (!patientDetailsPathRegex.test(location.pathname) || isProduction()) {
    return null;
  }

  const handleViewTypeChange = async (viewType: string) => {
    setShowOptions(false);

    if (viewType === selectedOption.value || isSaving) {
      return;
    }

    // Immediate feedback: a fullscreen spinner covers the save round-trip and
    // stays up until the reload happens (both can be slow connections-wise).
    setIsSaving(true);

    try {
      await userService.updateSelectedViewType(viewType);
    } catch {
      setIsSaving(false);
      return;
    }

    const portalPreferencesToChange = {
      ...portalPreferences,
      selectedViewType: viewType,
    };

    // Persist synchronously before reloading — the context's localStorage
    // effect would not get a chance to run before the page unloads.
    safeStorage.setItem(
      AUTH_PORTAL_PREFERENCES,
      JSON.stringify(portalPreferencesToChange)
    );
    changePortalPreferences(portalPreferencesToChange);

    mixpanelActions.track(`User Action: ChangeViewType (${viewType})`);

    // Reload so the patient page re-renders every section with the
    // configs re-ordered by the saved view type.
    window.location.reload();
  };

  return (
    <>
      {isSaving ? (
        <SavingOverlay>
          <Spin size="large" />
        </SavingOverlay>
      ) : null}

      <ViewTypeSelectorButton
        active={selectedOption.value !== DEFAULT_VIEW_TYPE}
        onClick={() => setShowOptions(true)}
      >
        {t("viewType_selector", { viewType: t(selectedOption.labelKey) })}{" "}
        <ArrowDown />
        <ViewTypeOptionsContainer ref={optionsRef} open={showOptions}>
          {showOptions
            ? viewTypeOptions.map((option) => (
                <ViewTypeOption
                  key={option.value}
                  selected={option.value === selectedOption.value}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleViewTypeChange(option.value);
                  }}
                >
                  {t(option.labelKey)}
                </ViewTypeOption>
              ))
            : null}
          {showOptions ? (
            <ViewTypeOptionsFooter>
              {t("viewType_pinnedNote")}
            </ViewTypeOptionsFooter>
          ) : null}
        </ViewTypeOptionsContainer>
      </ViewTypeSelectorButton>
    </>
  );
}
